import { useTranslation } from 'react-i18next'
import { PriceOrDiscountBlock, ServiceStatusBadge, type ServiceStatusBadgeTone } from '@/shared/components/cse'
import { formatIsoDateDisplay } from '@/shared/lib/datetime'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { sanitizeInternalTicketRefs } from '@/shared/lib/sanitizeInternalTicketRefs'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import type { GroupTrip, GroupTripStatus } from '@/features/group-trip/types'
import { HorizonMediaCard } from '../../../shared/components/HorizonMediaCard'

const MapPinIcon = createLazyThemedIcon('map-pin')

const STATUS_TONE: Record<GroupTripStatus, ServiceStatusBadgeTone> = {
  draft: 'neutral',
  open: 'open',
  closed: 'closed',
  completed: 'neutral',
  cancelled: 'closed',
}

export function HorizonGroupTripCard({
  trip,
  priority = false,
}: {
  trip: GroupTrip
  priority?: boolean
}) {
  const { t } = useTranslation()
  const detailTo = `/group-trips/${encodeURIComponent(trip.id)}`
  const priceValue =
    trip.price_cents != null
      ? formatMoneyCents(trip.price_cents)
      : trip.deposit_amount_cents != null
        ? formatMoneyCents(trip.deposit_amount_cents)
        : t(`group-trip.payment_mode.${trip.payment_mode}`)

  return (
    <HorizonMediaCard
      to={detailTo}
      title={trip.title}
      subtitle={t('horizon.group_trip.card.dates', {
        from: formatIsoDateDisplay(trip.trip_starts_at),
        to: formatIsoDateDisplay(trip.trip_ends_at),
      })}
      badges={<ServiceStatusBadge label={t(`group-trip.status.${trip.status}`)} tone={STATUS_TONE[trip.status]} />}
      details={<p className="m-0 line-clamp-3">{sanitizeInternalTicketRefs(trip.description)}</p>}
      price={
        <PriceOrDiscountBlock
          bare
          value={priceValue}
          helper={t(`group-trip.payment_mode.${trip.payment_mode}`)}
        />
      }
      actionLabel={t('group-trip.view')}
      actionAriaLabel={t('group-trip.view_aria', { title: trip.title })}
      fallbackIcon={MapPinIcon}
      priority={priority}
    />
  )
}
